import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import { RefreshDouble } from "iconoir-react";
import { Icon } from "@/design-system/icons/icon";
import { cn } from "@/lib/cn";

const variants = cva(
  "inline-flex h-[var(--control-height)] shrink-0 items-center justify-center gap-2 rounded-[var(--radius-pill)] px-5 text-sm font-semibold leading-none transition-[background-color,border-color,color,transform] active:translate-y-px disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-white hover:bg-primary-hover",
        secondary:
          "border border-line bg-surface text-foreground hover:border-line-strong hover:bg-hover",
        ghost: "text-foreground hover:bg-hover",
        danger: "bg-danger text-white hover:brightness-90",
      },
    },
    defaultVariants: { variant: "primary" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof variants> & { asChild?: boolean; loading?: boolean };
export function Button({
  asChild,
  className,
  loading,
  disabled,
  children,
  variant,
  ...props
}: ButtonProps) {
  if (asChild) {
    return (
      <Slot
        aria-busy={loading || undefined}
        className={cn(variants({ variant }), className)}
        {...props}
      >
        {children}
      </Slot>
    );
  }
  return (
    <button
      aria-busy={loading || undefined}
      className={cn(variants({ variant }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="grid size-5 place-items-center">
          <Icon className="animate-spin" glyph={RefreshDouble} size={18} />
        </span>
      )}
      {children}
    </button>
  );
}
